/**
 * Platform-team support queue — cross-tenant tickets raised from the Scout
 * widget (manually or by Scout). Update status / reply; requester-visible
 * actions notify the user in-platform via the bell.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LifeBuoy, Loader2, Lock, Send } from "lucide-react";
import {
  supportTicketsApi,
  type SupportTicketSummary,
} from "@/api/support-tickets";

const STATUS_FILTERS = [
  { key: "", label: "Active" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

const STATUS_BADGE: Record<string, string> = {
  open: "bg-accent/10 text-accent",
  in_progress: "bg-warning/15 text-warning",
  resolved: "bg-success/15 text-success",
  closed: "bg-surface-hover text-text-muted",
};

const PRIORITY_BADGE: Record<string, string> = {
  urgent: "bg-danger/15 text-danger",
  high: "bg-warning/15 text-warning",
  normal: "bg-surface-hover text-text-secondary",
  low: "bg-surface-hover text-text-muted",
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SupportTickets() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-tickets", statusFilter],
    queryFn: () => supportTicketsApi.adminList(statusFilter || undefined),
    refetchInterval: 60_000,
  });

  const { data: detail } = useQuery({
    queryKey: ["admin-support-ticket", selectedId],
    queryFn: () => supportTicketsApi.adminGet(selectedId!),
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-support-tickets"] });
    qc.invalidateQueries({ queryKey: ["admin-support-ticket", selectedId] });
  };

  const setStatus = useMutation({
    mutationFn: (status: string) => supportTicketsApi.adminSetStatus(selectedId!, status),
    onSuccess: invalidate,
  });

  const addComment = useMutation({
    mutationFn: () => supportTicketsApi.adminAddComment(selectedId!, reply.trim(), internal),
    onSuccess: () => {
      setReply("");
      setInternal(false);
      invalidate();
    },
  });

  const tickets: SupportTicketSummary[] = data?.tickets ?? [];

  return (
    <CenterPanel>
      <div className="mb-5 flex items-center gap-2">
        <LifeBuoy size={20} className="text-accent" />
        <h1 className="m-0 text-lg font-semibold text-text-primary">Support Tickets</h1>
      </div>

      <div className="mb-4 flex gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={cn(
              "rounded-full px-3 py-1 text-[12.5px] font-medium",
              statusFilter === f.key
                ? "bg-accent text-white"
                : "bg-surface-hover text-text-secondary hover:text-text-primary",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        {/* Queue */}
        <Card>
          <CardContent className="p-2">
            {isLoading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : tickets.length === 0 ? (
              <p className="m-0 p-6 text-center text-sm text-text-muted">No tickets.</p>
            ) : (
              tickets.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={cn(
                    "flex w-full flex-col gap-1 rounded-lg px-3 py-2.5 text-left hover:bg-surface-hover",
                    selectedId === t.id && "bg-surface-hover",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[13.5px] font-medium text-text-primary">
                      {t.subject}
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-[2px] text-[10.5px] font-semibold",
                        STATUS_BADGE[t.status],
                      )}
                    >
                      {t.status.replace("_", " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[11.5px] text-text-muted">
                    <span className="font-medium text-text-secondary">{t.companyName}</span>
                    <span>· {t.requesterName ?? "unknown"}</span>
                    {t.source === "scout" && <span>· via Scout</span>}
                    <span
                      className={cn(
                        "ml-auto rounded-full px-1.5 py-[1px] text-[10px] font-semibold",
                        PRIORITY_BADGE[t.priority] ?? PRIORITY_BADGE.normal,
                      )}
                    >
                      {t.priority}
                    </span>
                    <span>{formatWhen(t.updatedAt)}</span>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card>
          {!detail ? (
            <CardContent className="p-8 text-center text-sm text-text-muted">
              Select a ticket to view and respond.
            </CardContent>
          ) : (
            <>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-base">{detail.ticket.subject}</CardTitle>
                  <select
                    value={detail.ticket.status}
                    onChange={(e) => setStatus.mutate(e.target.value)}
                    disabled={setStatus.isPending}
                    className="rounded-md border border-border bg-bg-secondary px-2 py-1 text-[12.5px]"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <p className="m-0 text-[12px] text-text-muted">
                  {detail.ticket.companyName} · {detail.ticket.requesterName} ·{" "}
                  {detail.ticket.source === "scout" ? "raised by Scout" : "raised manually"} ·{" "}
                  {formatWhen(detail.ticket.createdAt)}
                  {detail.ticket.notifyChannel && detail.ticket.notifyChannel !== "in_app" && (
                    <> · updates via {detail.ticket.notifyChannel === "teams" ? "Teams DM" : "email"}</>
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="whitespace-pre-wrap rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-text-secondary">
                  {detail.ticket.description}
                </div>
                {detail.comments.map((c) => (
                  <div
                    key={c.id}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-[13px]",
                      c.internal
                        ? "border-warning/30 bg-warning/5"
                        : c.authorType === "platform_team"
                          ? "border-accent/25 bg-accent/5"
                          : "border-border bg-background",
                    )}
                  >
                    <p className="m-0 mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-text-muted">
                      {c.internal && <Lock size={10} />}
                      {c.authorType === "platform_team" ? c.authorName ?? "Platform team" : c.authorName ?? "Requester"}
                      {c.internal && " (internal note)"} · {formatWhen(c.createdAt)}
                    </p>
                    <p className="m-0 whitespace-pre-wrap text-text-primary">{c.content}</p>
                  </div>
                ))}
                <div className="space-y-2 border-t border-border pt-3">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={internal ? "Internal note (not visible to requester)…" : "Reply to requester (they'll be notified)…"}
                    rows={3}
                    className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-[13px] outline-none focus:ring-1 focus:ring-accent"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-[12px] text-text-secondary">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                      />
                      Internal note
                    </label>
                    <Button
                      size="sm"
                      onClick={() => addComment.mutate()}
                      disabled={!reply.trim() || addComment.isPending}
                    >
                      {addComment.isPending ? (
                        <Loader2 size={13} className="mr-1.5 animate-spin" />
                      ) : (
                        <Send size={13} className="mr-1.5" />
                      )}
                      {internal ? "Add note" : "Reply"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </CenterPanel>
  );
}
